import type {
  AdminDashboardSummary,
  AdminFileDownloadUrl,
  AdminFileStreamToken,
  AdminProjectFilesResult,
  AdminTenant,
  AdminTenantProjectsResult,
  ListAdminTenantsResult,
  ListTenantAuditLogsResult,
  UpdateTenantPlanInput,
} from '@terafile/shared'
import { apiFetch } from './api.js'

/**
 * 管理者ダッシュボード用 API クライアント。
 */

export type ListAdminTenantsParams = {
  plan?: 'FREE' | 'PRO' | 'EPHEMERAL'
  status?: 'ACTIVE' | 'SUSPENDED' | 'FROZEN'
  kind?: 'REGULAR' | 'EPHEMERAL'
  /** テナント名 / 所属ユーザーメアドの部分一致検索 */
  q?: string
  page?: number
  limit?: number
}

export async function listAdminTenants(
  params: ListAdminTenantsParams = {},
): Promise<ListAdminTenantsResult> {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) usp.set(k, String(v))
  }
  const qs = usp.toString()
  return apiFetch<ListAdminTenantsResult>(`/admin/tenants${qs ? `?${qs}` : ''}`)
}

export async function getAdminTenant(id: string): Promise<AdminTenant> {
  return apiFetch<AdminTenant>(`/admin/tenants/${id}`)
}

export async function suspendTenant(id: string): Promise<AdminTenant> {
  return apiFetch<AdminTenant>(`/admin/tenants/${id}/suspend`, { method: 'POST' })
}

export async function unsuspendTenant(id: string): Promise<AdminTenant> {
  return apiFetch<AdminTenant>(`/admin/tenants/${id}/unsuspend`, { method: 'POST' })
}

export async function updateTenantPlan(
  id: string,
  input: UpdateTenantPlanInput,
): Promise<AdminTenant> {
  return apiFetch<AdminTenant>(`/admin/tenants/${id}/plan`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export async function listTenantProjects(id: string): Promise<AdminTenantProjectsResult> {
  return apiFetch<AdminTenantProjectsResult>(`/admin/tenants/${id}/projects`)
}

export async function getAdminDashboardSummary(): Promise<AdminDashboardSummary> {
  return apiFetch<AdminDashboardSummary>('/admin/dashboard/summary')
}

// ----- ステルス: ファイル単位 -----

export async function listAdminProjectFiles(
  tenantId: string,
  projectId: string,
): Promise<AdminProjectFilesResult> {
  return apiFetch<AdminProjectFilesResult>(`/admin/tenants/${tenantId}/projects/${projectId}/files`)
}

export async function getAdminFileDownloadUrl(fileId: string): Promise<AdminFileDownloadUrl> {
  return apiFetch<AdminFileDownloadUrl>(`/admin/files/${fileId}/download-url`)
}

export async function getAdminFileStreamToken(fileId: string): Promise<AdminFileStreamToken> {
  return apiFetch<AdminFileStreamToken>(`/admin/files/${fileId}/stream-token`)
}

/**
 * ステルス閲覧用のサムネ URL を取得する。
 * - 動画: Cloudflare Stream の署名済みサムネ URL
 * - 画像: R2 原本の署名 URL（画像は専用サムネを持たないため原本で代替）
 *
 * 呼ぶたびに admin_audit_logs に `STEALTH_VIEW_THUMBNAIL` が記録されるため、
 * 一覧では `useInView` 等で「画面に入った行のみ」発火させること。
 */
export async function getAdminFileThumbnailUrl(fileId: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(`/admin/files/${fileId}/thumbnail-url`)
}

// ----- 監査ログ（テナント単位） -----

export type ListTenantAuditLogsParams = {
  page?: number
  limit?: number
}

export async function listTenantAuditLogs(
  tenantId: string,
  params: ListTenantAuditLogsParams = {},
): Promise<ListTenantAuditLogsResult> {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) usp.set(k, String(v))
  }
  const qs = usp.toString()
  return apiFetch<ListTenantAuditLogsResult>(
    `/admin/tenants/${tenantId}/audit-logs${qs ? `?${qs}` : ''}`,
  )
}
